/* V2 header: Milen stage-1-6 sticky nav, nav fade and desktop mega menu, with native keyboard,
   click/touch and theme-editor support. Custom-element lifecycle owns every listener. */
const EASE = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
// Hover intent (Milen 8 Oct, desktop navigation trapping at 1397x734): with no panel open, the pointer has to rest on a
// nav item OPEN_DELAY ms before its panel opens (crossing the row on the way to the icons or the page opens nothing);
// with one open, moving along the row switches at once. Leaving the row and panel closes after CLOSE_DELAY, so an
// overshoot can come back (toward the logo row or onto a plain link it still closes at once, as Milen's does).
const OPEN_DELAY = 150;
const CLOSE_DELAY = 250;
const desktopQuery = () => window.matchMedia('(min-width: 992px)');

// While a nav overlay is open, hide Google's merchant badge (fixed, z-index 2^31-1, above the mega panels).
// Shared with the mobile menu and the search overlay through window.XZV2Overlays.
function setOverlay(key, on) {
  const keys = window.XZV2Overlays || (window.XZV2Overlays = new Set());
  if (on) keys.add(key); else keys.delete(key);
  document.documentElement?.classList?.toggle('xz-v2-overlay-open', keys.size > 0);
}

/* Mega-panel artwork warm-up (see setupWarm). Loads the cards' own lazy <img>s WARM_LIMIT at a time: each is switched
   to eager, so it fetches exactly the srcset candidate the card shows and an opening panel paints it in its first
   frame. That first paint decodes every picture it shows, so the series artwork (JPEG sources) is requested as
   progressive JPEG (snippets/xz-v2-mobile-navigation-image.liquid): Shopify's CDN otherwise sends WebKit JPEG XL at
   360-480w, which WebKit decoded on the main thread (~10-17ms each in that paint), stalling the first open of Shop By
   Series 130-370ms (m18-nav perf, 9 Oct 2026). No img.decode() (WebKit decodes JPEG XL synchronously there: ~100-150ms
   per picture) and no fetchpriority=low (WebKit then held 10-70KB images back 300-900ms; Chrome already loads images
   outside the viewport at low priority); see xz-v2-mobile-navigation.js, which carries the same queue (each module
   stays self-contained). */
const WARM_LIMIT = 6;
// A mouse moving up (WARM_RISE px within 150ms) within WARM_APPROACH px below the shown nav is heading for it.
const WARM_APPROACH = 160;
const WARM_RISE = 24;
function warmQueue(limit) {
  const queue = [], seen = new WeakSet();
  let active = 0, running = false;
  const pump = () => {
    while (running && active < limit && queue.length) {
      const img = queue.shift();
      if (!img.isConnected || (img.complete && img.loading !== 'lazy')) continue;
      img.loading = 'eager';
      if (img.complete) continue;
      active++;
      let settled = false, timer = 0;
      const done = () => { if (settled) return; settled = true; clearTimeout(timer); active--; pump(); };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      // Frees the slot if an image never settles.
      timer = setTimeout(done, 10000);
    }
  };
  return {
    // Queues images not seen before; `front` also moves queued ones ahead of the rest.
    add(imgs, front = false) {
      const next = [];
      for (const img of imgs) {
        if (!seen.has(img)) { seen.add(img); next.push(img); }
        else if (front && queue.includes(img)) { queue.splice(queue.indexOf(img), 1); next.push(img); }
      }
      if (front) queue.unshift(...next); else queue.push(...next);
      pump();
    },
    run(on) { running = on; pump(); },
  };
}

class XinzuoV2Header extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.nav = this.querySelector('.xz-top-nav-wrp');
    this.line = this.querySelector('.xz-line-tnav');
    this.row = this.querySelector('[data-v2-nav-row]');
    this.functionRow = this.querySelector('[data-v2-function-row]');
    this.desktop = desktopQuery();
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverEnabled = this.dataset.v2Hover !== 'false';
    this.open = null;
    this.lastY = window.scrollY;
    // Last input modality: focus only holds the sticky header open for keyboard users. Pointer
    // interactions (and dialogs restoring focus to a header icon after a tap/click) must not pin it.
    if (typeof this.keyboardModality !== 'boolean') this.keyboardModality = false;
    document.addEventListener('keydown', (event) => { if (!event.metaKey && !event.ctrlKey && !event.altKey) this.keyboardModality = true; }, { capture: true, signal });
    document.addEventListener('pointerdown', () => { this.keyboardModality = false; }, { capture: true, signal });

    this.setupHeight(signal);
    this.setupSticky(signal);
    this.setupFade(signal);
    this.setupMega(signal);
    this.setupWarm(signal);
    this.setupMenu(signal);
    this.setupDialogs(signal);
    this.setupAccount(signal);
    this.setupEditor(signal);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resize?.disconnect();
    this.heroResize?.disconnect();
    this.hostObserver?.disconnect();
    this.menuObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);
    this.frame = null;
    setOverlay('mega', false);
  }

  /* ---------- layout height for sticky consumers ---------- */
  setupHeight(signal) {
    const placeholder = this.querySelector('.xz-nav-placeholder');
    const sync = () => {
      const height = placeholder.offsetHeight;
      this.headerHeight = height;
      document.body.style.setProperty('--header-height', `${height}px`);
      document.body.style.setProperty('--header-group-height', `${height}px`);
    };
    this.resize?.disconnect();
    this.resize = new ResizeObserver(sync);
    this.resize.observe(placeholder);
    sync();
    signal.addEventListener('abort', () => this.resize?.disconnect());
  }

  /* ---------- hide on scroll down, show on scroll up (Milen: 128px gate, 8px threshold) ---------- */
  setHidden(hidden) {
    this.navHidden = hidden;
    this.nav.style.transform = hidden ? 'translateY(-100%)' : 'translateY(0)';
    // data-sticky-state and --v2-visible-header-height only feed Horizon's sticky product blocks. The
    // bundled legacy CSS matches both through body-level :has() rules, so each write restyled the
    // whole document (10-26ms per toggle on home/PLP/PDP) and the slide started a frame or more late.
    // No v2 page renders such a block, so the writes happen only when one exists; otherwise the
    // server value (active) and the CSS default stay put.
    const consumer = document.querySelector('.sticky-content, .sticky-content--desktop, .slideshow-controls__thumbnails-container');
    if (consumer) {
      this.dataset.stickyState = hidden ? 'idle' : 'active';
      document.body.style.setProperty('--v2-visible-header-height', hidden ? '32px' : 'var(--header-height)');
    } else if (this.dataset.stickyState !== 'active' || document.body.style.getPropertyValue('--v2-visible-header-height')) {
      this.dataset.stickyState = 'active';
      document.body.style.removeProperty('--v2-visible-header-height');
    }
  }

  setupSticky(signal) {
    // Milen 2 Oct (G1): on a desktop PDP the nav stays shown until the hero's bottom edge is HERO_OFFSET px
    // from the top of the viewport (his HERO_OFFSET knob); below 992px and on every other page the 128px gate.
    // The hero is looked up lazily (the header connects before <main> is parsed; the editor can replace the
    // section) and scoped to the v2 PDP element. Its bottom is read once per scroll frame, before any write in
    // the frame, so the read never forces a layout; a ResizeObserver plus resize/load re-run the check when the
    // hero grows (gallery images, Judge.me badge) without a scroll.
    const GATE = 128, HERO_OFFSET = 256, THRESHOLD = 8;
    const request = () => { if (!this.frame) this.frame = requestAnimationFrame(update); };
    const heroGate = (y) => {
      if (!this.desktop.matches) return GATE;
      if (!this.hero?.isConnected && !this.heroAbsent) {
        this.hero = document.querySelector('xz-v2-pdp .xz-pdp-hero');
        // Once the document is parsed, a page without the PDP hero stops looking (until the editor reloads a section).
        this.heroAbsent = !this.hero && document.readyState !== 'loading';
        this.heroResize?.disconnect();
        this.heroResize = null;
        if (this.hero && 'ResizeObserver' in window) { this.heroResize = new ResizeObserver(request); this.heroResize.observe(this.hero); }
      }
      return this.hero ? Math.max(GATE, y + this.hero.getBoundingClientRect().bottom - HERO_OFFSET) : GATE;
    };
    const update = () => {
      this.frame = null;
      const y = window.scrollY;
      const gate = heroGate(y);
      this.line.style.opacity = y > GATE ? '1' : '0';
      if (y <= gate) { this.setHidden(false); this.lastY = y; return; }
      const delta = y - this.lastY;
      if (Math.abs(delta) < THRESHOLD) return;
      const keyboardFocus = this.keyboardModality && this.nav.contains(document.activeElement);
      const busy = this.open || this.menuOpen || this.querySelector('.xz-v2-header-account[open]') || keyboardFocus;
      this.setHidden(delta > 0 && !busy);
      this.lastY = y;
    };
    this.addEventListener('focusin', () => { if (this.keyboardModality) this.setHidden(false); }, { signal });
    window.addEventListener('scroll', request, { passive: true, signal });
    window.addEventListener('resize', request, { passive: true, signal });
    window.addEventListener('load', request, { once: true, signal });
    this.desktop.addEventListener('change', request, { signal });
    document.addEventListener('shopify:section:load', () => { this.heroAbsent = false; request(); }, { signal });
    signal.addEventListener('abort', () => { this.heroResize?.disconnect(); this.heroResize = null; this.hero = null; this.heroAbsent = false; });
    update();
  }

  /* ---------- nav fade: hovered or open item full, siblings 0.4 (desktop only) ---------- */
  setupFade(signal) {
    const groups = [
      [this.row, [...this.row.querySelectorAll('.xz-nav-btn-wrp, .xz-v2-mega-trigger')]],
      [this.functionRow, [...this.functionRow.querySelectorAll(':scope > .ff-nav-fn-btn, :scope > header-actions .ff-nav-fn-btn, :scope > details.ff-nav-fn-btn')]],
    ];
    this.fadeGroups = groups;
    // Every read first, then only the writes that change something: a touch tap fires mouseenter too, and the old
    // write-read-write loop forced a style pass per group inside the tap (56ms of a 260ms frame on a 1024 tablet).
    const apply = () => {
      const desktop = this.desktop.matches;
      const transition = this.reduced.matches ? 'none' : 'opacity 0.15s ease';
      const writes = [];
      for (const [, items] of groups) {
        if (!desktop) { for (const item of items) writes.push([item, '', '']); continue; }
        const active = items.find((item) => item.getAttribute('aria-expanded') === 'true');
        const lit = active || items.find((item) => item.matches(':hover'));
        for (const item of items) writes.push([item, transition, !lit || item === lit ? '1' : '0.4']);
      }
      for (const [item, t, opacity] of writes) {
        if (item.style.transition !== t) item.style.transition = t;
        if (item.style.opacity !== opacity) item.style.opacity = opacity;
      }
    };
    this.applyFade = apply;
    for (const [container, items] of groups) {
      for (const item of items) item.addEventListener('mouseenter', apply, { signal });
      container.addEventListener('mouseleave', () => requestAnimationFrame(apply), { signal });
    }
    this.desktop.addEventListener('change', apply, { signal });
    apply();
  }

  /* ---------- desktop mega menu ---------- */
  panels() { return [...this.querySelectorAll('[data-v2-mega-panel]')]; }
  triggers() { return [...this.querySelectorAll('.xz-v2-mega-trigger')]; }
  panelFor(trigger) { return trigger && document.getElementById(trigger.getAttribute('aria-controls')); }
  triggerFor(panel) { return this.triggers().find((t) => t.getAttribute('aria-controls') === panel.id); }

  fillPanels(force = false) {
    this.adopted ||= new Map();
    for (const panel of this.panels()) {
      const key = panel.dataset.v2MegaPanel;
      const template = [...document.querySelectorAll('template[data-v2-desktop-panel]')].find((node) => node.dataset.v2DesktopPanel === key);
      // Full markup identity (not length): same-length editor edits must refill too.
      const signature = template ? template.dataset.sectionId + ':' + template.innerHTML : '';
      const trigger = this.triggerFor(panel);
      if (force || panel.v2Signature !== signature || !panel.childElementCount) {
        if (this.open?.panel === panel) this.hide();
        // Each panel adopts its template once per page and keeps the nodes: a refill after a morph (cart hydration
        // puts the server's empty panel back) reinserts the same elements, so their images stay loaded instead of being
        // cloned and loaded again. Only changed markup (editor) makes new ones. Keyed by the panel's id (one per nav
        // item), not the group key: two items on one group (a duplicated block) must not share nodes, or each refill
        // would move them from one panel to the other and the observer would refill again, forever.
        let adopted = this.adopted.get(panel.id);
        if (force || adopted?.signature !== signature) {
          adopted = { signature, nodes: template ? [...template.content.cloneNode(true).childNodes] : [] };
          this.adopted.set(panel.id, adopted);
        }
        panel.replaceChildren(...adopted.nodes);
        panel.v2Signature = signature;
      }
      if (!trigger) continue;
      // Without a panel the trigger stays the plain link the server rendered.
      trigger.dataset.v2Empty = String(!panel.childElementCount);
      if (panel.childElementCount) {
        trigger.setAttribute('role', 'button');
        if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', String(this.open?.trigger === trigger));
      } else {
        trigger.removeAttribute('role');
        trigger.removeAttribute('aria-expanded');
      }
    }
    this.syncEditorOwners();
  }

  /* Theme editor: each card block renders twice (mobile grid + desktop template). Only the copy for the
     current breakpoint carries data-shopify-editor-block, so the editor overlay never lands on a hidden copy. */
  syncEditorOwners() {
    if (!window.Shopify?.designMode) return;
    const desktop = this.desktop.matches;
    const owners = new Map();
    for (const card of document.querySelectorAll('[data-v2-mobile-block-id]')) {
      if (card.closest('template')) continue;
      const value = card.getAttribute('data-shopify-editor-block') || card.dataset.v2EditorBlock;
      if (value) owners.set(card.dataset.v2MobileBlockId, value);
    }
    for (const card of document.querySelectorAll('[data-v2-mobile-block-id]')) {
      if (card.closest('template')) continue;
      const value = owners.get(card.dataset.v2MobileBlockId);
      if (!value) continue;
      const inPanel = !!card.closest('[data-v2-mega-panel]');
      card.dataset.v2EditorBlock = value;
      if (inPanel === desktop) card.setAttribute('data-shopify-editor-block', value);
      else card.removeAttribute('data-shopify-editor-block');
    }
  }

  show(trigger, { focus = false, pinned = false, instant = false } = {}) {
    clearTimeout(this.openTimer);
    if (!this.desktop.matches) return;
    clearTimeout(this.closeTimer);
    const panel = this.panelFor(trigger);
    if (!panel || !panel.childElementCount) return;
    this.closeMenu();
    if (this.open && this.open.trigger !== trigger) this.hide(this.open.trigger, true);
    if (this.open?.trigger === trigger) {
      this.open.pinned = this.open.pinned || pinned;
      if (focus) this.focusFirst(panel);
      return;
    }
    this.open = { trigger, panel, pinned };
    setOverlay('mega', true);
    trigger.setAttribute('aria-expanded', 'true');
    this.setHidden(false);
    this.warm(panel, true, this.warmTouch);
    // The panel opens at its final style and the entrance runs as two Web Animations (compositor opacity and
    // transform), so nothing forces a style and layout pass inside the hover or tap (it used to: a start state,
    // `void offsetWidth`, then transitions; 44ms of forced work on a 1024 tablet). hide() returns a scrolled
    // panel to its top.
    // Own animations are tracked: getAnimations() would force a style pass here.
    panel.v2Anims?.forEach((animation) => animation.cancel());
    panel.v2Anims = null;
    panel.style.transition = 'none';
    panel.style.opacity = '1';
    panel.style.transform = 'translateY(0)';
    panel.hidden = false;
    panel.classList.add('is-open');
    if (!this.reduced.matches && !instant && typeof panel.animate === 'function') {
      panel.v2Anims = [
        panel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: EASE }),
        panel.animate([{ transform: 'translateY(-24px)' }, { transform: 'translateY(0)' }], { duration: 500, easing: EASE }),
      ];
    }
    this.applyFade?.();
    if (focus) this.focusFirst(panel);
  }

  hide(trigger = this.open?.trigger, instant = true, returnFocus = false) {
    clearTimeout(this.closeTimer);
    if (!trigger || this.open?.trigger !== trigger) return;
    const { panel } = this.open;
    this.open = null;
    setOverlay('mega', false);
    trigger.setAttribute('aria-expanded', 'false');
    panel.v2Anims?.forEach((animation) => animation.cancel());
    panel.v2Anims = null;
    // Chrome keeps a hidden element's scroll offset: a scrolled panel goes back to its top while still visible
    // (only then, so an unscrolled close forces no layout). The grid container is the scroller (header CSS).
    if (panel.v2Scrolled) {
      panel.scrollTop = 0;
      const container = panel.querySelector('.xz-dtp-dropdown-container');
      if (container) container.scrollTop = 0;
      panel.v2Scrolled = false;
    }
    panel.style.transition = 'none';
    panel.style.opacity = '0';
    panel.style.transform = 'translateY(-24px)';
    panel.classList.remove('is-open');
    panel.hidden = true;
    this.applyFade?.();
    if (returnFocus) trigger.focus({ preventScroll: true });
  }

  scheduleClose() {
    if (!this.open || this.open.pinned) return;
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.hide(), CLOSE_DELAY);
  }

  // Hover on a trigger: switch at once while a panel is open (or still in its close grace), else open after OPEN_DELAY.
  intend(trigger) {
    clearTimeout(this.openTimer);
    if (this.open) this.show(trigger);
    else this.openTimer = setTimeout(() => this.show(trigger), OPEN_DELAY);
  }

  // Queues every panel's artwork, `first` ahead of the rest (see setupWarm). `keep`: a panel opened, or a nav item has
  // keyboard focus or a press, so the queue runs to the end; otherwise it runs only while the mouse is near the nav.
  // `only`: just `first` (a touch press at 992px and up, e.g. an iPad: no hover tells which panel comes next, so a tap
  // loads the panel it opens, as before, not all four, ~1.8MB at DPR 2).
  warm(first, keep = false, only = false) {
    if (!this.desktop.matches) return;
    this.warmer ||= warmQueue(WARM_LIMIT);
    if (keep) this.warmKeep = true;
    if (!only) for (const panel of this.panels()) this.warmer.add(panel.querySelectorAll('img'));
    if (first) this.warmer.add(first.querySelectorAll('img'), true);
    this.warmer.run(true);
  }

  // The panel whose nav item is horizontally nearest to x.
  nearestPanel(x) {
    let best = null, distance = Infinity;
    for (const trigger of this.triggers()) {
      const box = trigger.getBoundingClientRect();
      const d = x < box.left ? box.left - x : x > box.right ? x - box.right : 0;
      if (d < distance && this.panelFor(trigger)?.childElementCount) { best = trigger; distance = d; }
    }
    return this.panelFor(best);
  }

  /* Panel artwork is lazy and the panels stay display:none until opened, so each image used to load only when its
     panel opened (blank tiles for up to ~150ms at DPR 2 on a fast link, ~800ms at 10 Mbit/s). Nothing loads with the
     page (PageSpeed and visitors who never go near the nav are unaffected). It loads on intent instead: a mouse moving
     up toward the shown nav (WARM_APPROACH px below it) or over the header starts the panel nearest the pointer, then
     the others; pointing at a nav item moves its panel to the front; its press or focus, or a panel opening, also lets
     the queue finish (a touch press queues only its own panel). Until a panel has opened, the queue pauses when the
     mouse moves away (below the band or out of the window, e.g. up to the browser's tabs), so a pass over the header
     costs at most the images already started. */
  setupWarm(signal) {
    let samples = [];
    const pause = () => {
      samples = [];
      if (!this.warmIntent) return;
      this.warmIntent = false;
      if (!this.warmKeep) this.warmer?.run(false);
    };
    document.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || !this.desktop.matches || this.warmKeep) return;
      const y = event.clientY;
      // A hidden nav (scrolled away) has nothing to open: a mouse going up there is heading for the browser's tabs.
      const top = this.headerHeight || 0;
      if (this.navHidden || y > top + WARM_APPROACH) { pause(); return; }
      if (this.warmIntent) return;
      const now = event.timeStamp;
      samples.push([now, y]);
      while (now - samples[0][0] > 150) samples.shift();
      if (y <= top || samples[0][1] - y >= WARM_RISE) {
        this.warmIntent = true;
        this.warm(this.nearestPanel(event.clientX));
      }
    }, { passive: true, signal });
    document.addEventListener('mouseout', (event) => { if (!event.relatedTarget) pause(); }, { passive: true, signal });
  }

  focusables(panel) { return [...panel.querySelectorAll('a[href], button:not([disabled])')]; }
  focusFirst(panel) { this.focusables(panel)[0]?.focus({ preventScroll: true }); }

  setupMega(signal) {
    this.fillPanels();
    // Section rendering (cart hydration, editor) morphs the header back to its server markup.
    this.hostObserver?.disconnect();
    this.hostObserver = new MutationObserver(() => {
      let reopen = null;
      if (this.open && (this.open.panel.hidden || !this.open.panel.childElementCount)) {
        reopen = { key: this.open.panel.dataset.v2MegaPanel, pinned: this.open.pinned };
        this.open.trigger.setAttribute('aria-expanded', 'false');
        this.open = null;
        setOverlay('mega', false);
      }
      if (this.panels().some((panel) => !panel.childElementCount)) this.fillPanels();
      // Cart hydration morphs the header back to server markup; keep a panel the visitor is using open.
      if (reopen && this.desktop.matches) {
        const trigger = this.triggers().find((t) => t.dataset.v2MegaKey === reopen.key);
        const container = this.panelFor(trigger)?.querySelector('.xz-dtp-dropdown-container');
        const using = reopen.pinned || trigger?.matches(':hover') || container?.matches(':hover') || this.row.matches(':hover');
        if (trigger && using) this.show(trigger, { pinned: reopen.pinned, instant: true });
      }
    });
    for (const panel of this.panels()) {
      this.hostObserver.observe(panel, { childList: true });
      // Capture: the grid container scrolls (its scroll events do not bubble), the shell only in old CSS.
      panel.addEventListener('scroll', () => { panel.v2Scrolled = true; }, { passive: true, capture: true, signal });
    }
    document.addEventListener('shopify:section:load', () => this.fillPanels(true), { signal });

    const isMouse = (event) => event.pointerType === 'mouse' || event.pointerType === 'pen' && this.desktop.matches;
    const inside = (node) => node instanceof Node && (this.row.contains(node) || this.line.contains(node) ||
      this.panels().some((panel) => panel.querySelector('.xz-dtp-dropdown-container')?.contains(node)));

    for (const trigger of this.triggers()) {
      trigger.addEventListener('pointerdown', (event) => {
        this.warmTouch = event.pointerType === 'touch';
        this.warm(this.panelFor(trigger), true, this.warmTouch);
      }, { signal, passive: true });
      trigger.addEventListener('focus', () => {
        // Keyboard focus queues every panel (Tab walks the row); focus from a tap only the tapped panel.
        let keyboard = true;
        try { keyboard = trigger.matches(':focus-visible'); } catch (e) {}
        this.warm(this.panelFor(trigger), true, !keyboard && this.warmTouch);
      }, { signal, passive: true });
      trigger.addEventListener('pointerenter', (event) => {
        // Pointing at an item moves its panel to the front of a started queue (no open with open_on_hover off).
        if (isMouse(event) && this.warmer) this.warm(this.panelFor(trigger));
        if (this.hoverEnabled && isMouse(event)) this.intend(trigger);
        else clearTimeout(this.closeTimer);
      }, { signal });
      trigger.addEventListener('pointerleave', () => clearTimeout(this.openTimer), { signal });
      trigger.addEventListener('click', (event) => {
        // Modified clicks keep native link behaviour (new tab/window).
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const panel = this.panelFor(trigger);
        // No panel (no matching group, or below 992): the trigger is a plain link.
        if (!this.desktop.matches || !panel?.childElementCount) return;
        event.preventDefault();
        const keyboard = event.detail === 0;
        if (this.open?.trigger === trigger) {
          if (this.open.pinned || keyboard || !this.hoverEnabled) this.hide(trigger, true, keyboard);
          else this.open.pinned = true;
        } else this.show(trigger, { pinned: true, focus: false });
      }, { signal });
      trigger.addEventListener('keydown', (event) => {
        this.warmTouch = false;
        const panel = this.panelFor(trigger);
        if (!panel?.childElementCount || !this.desktop.matches) return;
        if (event.key === ' ' || event.key === 'Spacebar') { event.preventDefault(); trigger.click(); }
        else if (event.key === 'ArrowDown') { event.preventDefault(); this.show(trigger, { pinned: true, focus: true }); }
        else if (event.key === 'Tab' && !event.shiftKey && this.open?.trigger === trigger) { event.preventDefault(); this.focusFirst(panel); }
      }, { signal });
    }

    // Plain nav links close any open panel immediately (Milen).
    for (const link of this.row.querySelectorAll('.xz-nav-btn-wrp')) {
      link.addEventListener('pointerenter', (event) => { if (isMouse(event) && this.open && !this.open.pinned) this.hide(); }, { signal });
    }
    // The panel stays open while the pointer is over the nav row, line or grid (Milen's grid reaches the window's
    // bottom edge on short windows; scrolling on past its end closes it, see `through` below). Leaving the row or line
    // upward or sideways (logo row, header chrome) closes at once, as Milen's does. Every other exit (down from the
    // row into the grid, out of the grid in any direction, out of the window) closes after CLOSE_DELAY, which bridges
    // the row-to-grid gap and lets an overshoot come back.
    const leave = (event, edge) => {
      if (!this.desktop.matches || !isMouse(event) || inside(event.relatedTarget) || !this.open || this.open.pinned) return;
      const toHeader = edge !== this.row && edge !== this.line ? false
        : event.relatedTarget instanceof Node && this.contains(event.relatedTarget) && event.clientY < edge.getBoundingClientRect().bottom - 1;
      if (toHeader) this.hide();
      else this.scheduleClose();
    };
    const enter = () => clearTimeout(this.closeTimer);
    this.row.addEventListener('pointerleave', (event) => leave(event, this.row), { signal });
    this.line.addEventListener('pointerleave', (event) => leave(event, this.line), { signal });
    this.row.addEventListener('pointerenter', enter, { signal });
    this.line.addEventListener('pointerenter', enter, { signal });
    for (const panel of this.panels()) {
      panel.addEventListener('pointerover', (event) => { if (panel.querySelector('.xz-dtp-dropdown-container')?.contains(event.target)) enter(); }, { signal });
      panel.addEventListener('pointerout', (event) => {
        const container = panel.querySelector('.xz-dtp-dropdown-container');
        if (container?.contains(event.target) && !container.contains(event.relatedTarget)) leave(event, container);
      }, { signal });
      panel.addEventListener('keydown', (event) => {
        if (!this.open || this.open.panel !== panel) return;
        const items = this.focusables(panel);
        const index = items.indexOf(document.activeElement);
        if (event.key === 'Escape') { event.preventDefault(); this.hide(this.open.trigger, true, true); }
        else if (event.key === 'Tab' && event.shiftKey && index === 0) { event.preventDefault(); this.open.trigger.focus(); }
        else if (event.key === 'Tab' && !event.shiftKey && index === items.length - 1) {
          event.preventDefault();
          const trigger = this.open.trigger;
          const order = [...this.row.querySelectorAll('a[href], button')];
          this.hide(trigger);
          (order[order.indexOf(trigger) + 1] || trigger).focus();
        }
      }, { signal });
    }
    // Escape closes a panel however it was opened (hover leaves focus on the page; Safari never focuses
    // clicked buttons). Focus returns to the trigger only when it was inside the header or panel.
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.open || event.defaultPrevented) return;
      event.preventDefault();
      const active = document.activeElement;
      const focusInside = active && active !== document.body && (this.contains(active));
      this.hide(this.open.trigger, true, focusInside);
    }, { signal });
    // Focus leaving the nav row and panel closes a keyboard-opened panel.
    this.addEventListener('focusout', (event) => {
      if (!this.open) return;
      const next = event.relatedTarget;
      if (next && (inside(next) || this.open.panel.contains(next))) return;
      if (!next && document.hasFocus() && this.open.pinned) return;
      if (next) this.hide();
    }, { signal });
    // Outside click/tap closes an open panel. The blurred panel shell lets pointer events through
    // (pointer-events:none, as Milen's hover build), so a press on the blur would otherwise activate the
    // hidden page underneath: treat it as dismiss-only and swallow that press's mouse/click events.
    // Presses on the visible header chrome (logo, icons, nav row) keep working.
    let swallowUntil = 0;
    const swallow = (event) => {
      if (performance.now() > swallowUntil) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.type === 'click') swallowUntil = 0;
    };
    window.addEventListener('pointerdown', (event) => {
      swallowUntil = 0;
      if (!this.open) return;
      const container = this.open.panel.querySelector('.xz-dtp-dropdown-container');
      if (this.row.contains(event.target) || container?.contains(event.target)) return;
      const underBlur = !this.contains(event.target);
      this.hide();
      if (!underBlur) return;
      swallowUntil = performance.now() + 1000;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal });
    for (const type of ['mousedown', 'mouseup', 'pointerup', 'click']) window.addEventListener(type, swallow, { capture: true, signal });
    window.addEventListener('pointercancel', () => { swallowUntil = 0; }, { capture: true, signal });
    // Wheel over the nav row scrolls the open panel (Milen): its grid container, the scroller since the window cap
    // (the shell when the container does not overflow, e.g. with an older cached stylesheet).
    const wheel = (event) => {
      if (!this.open || event.ctrlKey || !event.cancelable) return;
      event.preventDefault();
      const container = this.open.panel.querySelector('.xz-dtp-dropdown-container');
      const scroller = container && container.scrollHeight > container.clientHeight ? container : this.open.panel;
      const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientHeight : 1;
      scroller.scrollTop += event.deltaY * factor;
    };
    this.row.addEventListener('wheel', wheel, { passive: false, signal });
    this.line.addEventListener('wheel', wheel, { passive: false, signal });
    // Scrolling on past the end closes the panel (Milen 8 Oct, "desktop navigation trapping" at 1397x734; his panels
    // reach the window's bottom edge, so there is no page below them to move to). With the panel's end reached, the
    // next downward scroll after a 300ms pause (a new gesture: a flick's momentum never closes it) closes the panel,
    // pinned or not, and the page scrolls from there. A panel too short to scroll closes on the first downward scroll.
    let endAt = 0, lastWheel = 0, past = 0;
    const through = (event) => {
      if (!this.open || event.ctrlKey || event.deltaY <= 0) { endAt = 0; past = 0; return; }
      const panel = this.open.panel;
      const container = panel.querySelector('.xz-dtp-dropdown-container');
      const scroller = container && container.scrollHeight > container.clientHeight ? container : panel;
      const room = scroller.scrollHeight - scroller.clientHeight;
      const now = event.timeStamp, gap = now - lastWheel;
      lastWheel = now;
      if (room > 1 && scroller.scrollTop < room - 1) { endAt = 0; past = 0; return; }
      if (room > 1 && !endAt) { endAt = now; past = 0; return; }
      if (room > 1 && gap <= 300 && !past) return;
      past += event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * scroller.clientHeight : event.deltaY;
      if (past >= 40) { endAt = 0; past = 0; this.hide(); }
    };
    for (const panel of this.panels()) panel.addEventListener('wheel', through, { passive: true, signal });
    this.row.addEventListener('wheel', through, { passive: true, signal });
    this.line.addEventListener('wheel', through, { passive: true, signal });
    this.desktop.addEventListener('change', () => { if (!this.desktop.matches && this.open) this.hide(); this.syncEditorOwners(); }, { signal });
    // Following a link inside a panel closes it (instant feedback before navigation).
    for (const panel of this.panels()) panel.addEventListener('click', (event) => { if (event.target.closest('a[href]')) this.hide(); }, { signal });
  }

  /* ---------- desktop menu dropdown (Milen 1_8 round 1) ---------- */
  /* From 992px the menu button opens a dropdown of the menu's direct links (below 992px: the mobile menu). Its links
     are the mobile menu's link sections, one data source: each emits a <template data-v2-desktop-links> (sections/
     xz-v2-mobile-navigation-links.liquid) that is adopted here in group order, with the gold SHOP ALL link moved to the
     end as the button. Dylan's desktop-menu rules (9 Oct): click only (no hover), no close button, the open trigger is
     gold. It closes on its trigger, an outside press (which still does what it presses: the page is not covered),
     Escape, focus leaving it, following one of its links, a mega panel opening (and opening it closes a mega panel or
     the account menu). Button semantics come from syncMenuMode (setupDialogs). The panel follows the button in the
     DOM, so Tab and Shift+Tab walk in and out natively; ArrowDown on the button opens it and focuses the first link. */
  menuPanel() { return this.querySelector('[data-v2-menu-panel]'); }
  menuButton() { return this.querySelector('[data-v2-menu]'); }

  fillMenu(force = false) {
    const panel = this.menuPanel();
    if (!panel) return;
    const templates = [...document.querySelectorAll('template[data-v2-desktop-links]')];
    const signature = templates.map((t) => t.dataset.sectionId + ':' + t.innerHTML).join('\n');
    if (!force && panel.v2Signature === signature && panel.childElementCount) return;
    if (this.menuOpen && (force || panel.v2Signature !== signature)) this.closeMenu();
    // Adopted once and kept (a morph refill reinserts the same nodes); new nodes only for changed markup (editor).
    if (force || this.menuNodes?.signature !== signature) {
      const nodes = templates.flatMap((t) => [...t.content.cloneNode(true).children]);
      const buttons = nodes.filter((node) => node.matches('.xz-v2-menu-dd-cta'));
      this.menuNodes = { signature, nodes: [...nodes.filter((node) => !buttons.includes(node)), ...buttons] };
    }
    panel.replaceChildren(...this.menuNodes.nodes);
    panel.v2Signature = signature;
  }

  openMenu({ focus = false, instant = false } = {}) {
    const panel = this.menuPanel(), button = this.menuButton();
    if (!panel || !button || !this.desktop.matches || !panel.childElementCount) return false;
    if (this.open) this.hide();
    const account = this.querySelector('.xz-v2-header-account[open]');
    if (account) account.open = false;
    if (!this.menuOpen) {
      this.menuOpen = true;
      button.setAttribute('aria-expanded', 'true');
      this.setHidden(false);
      panel.v2Anims?.forEach((animation) => animation.cancel());
      panel.v2Anims = null;
      panel.hidden = false;
      // Milen's mega-panel entrance, shortened for a small panel: compositor opacity and transform only.
      if (!this.reduced.matches && !instant && typeof panel.animate === 'function') {
        panel.v2Anims = [panel.animate([{ opacity: 0, transform: 'translateY(-8px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 200, easing: EASE })];
      }
      this.applyFade?.();
    }
    if (focus) panel.querySelector('a[href]')?.focus({ preventScroll: true });
    return true;
  }

  closeMenu(returnFocus = false) {
    if (!this.menuOpen) return;
    const panel = this.menuPanel(), button = this.menuButton();
    this.menuOpen = false;
    button?.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.v2Anims?.forEach((animation) => animation.cancel());
      panel.v2Anims = null;
      // Back to its top only when it was scrolled (short windows), so a plain close forces no layout.
      if (panel.v2Scrolled) { panel.scrollTop = 0; panel.v2Scrolled = false; }
      panel.hidden = true;
    }
    this.applyFade?.();
    if (returnFocus) button?.focus({ preventScroll: true });
  }

  setupMenu(signal) {
    const panel = this.menuPanel();
    if (!panel) return;
    this.menuOpen = false;
    this.fillMenu();
    // Cart hydration morphs the header back to its server markup (an empty, hidden panel; the button's script-set
    // attributes removed): refill it, restore the button and keep an open dropdown open.
    this.menuObserver?.disconnect();
    this.menuObserver = new MutationObserver(() => {
      const reopen = this.menuOpen && (panel.hidden || !panel.childElementCount);
      if (reopen) this.menuOpen = false;
      if (!panel.childElementCount) this.fillMenu();
      this.syncMenuMode?.();
      if (reopen) this.openMenu({ instant: true });
    });
    this.menuObserver.observe(panel, { childList: true });
    document.addEventListener('shopify:section:load', () => { this.fillMenu(true); this.syncMenuMode?.(); }, { signal });
    panel.addEventListener('scroll', () => { panel.v2Scrolled = true; }, { passive: true, signal });
    // Following a link closes it (instant feedback before navigation).
    panel.addEventListener('click', (event) => { if (event.target.closest('a[href]')) this.closeMenu(); }, { signal });
    // An outside press closes it and still does what it presses (the page is not covered, unlike the mega panels).
    window.addEventListener('pointerdown', (event) => {
      if (!this.menuOpen || !(event.target instanceof Node)) return;
      if (panel.contains(event.target) || this.menuButton()?.contains(event.target)) return;
      this.closeMenu();
    }, { capture: true, signal });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.menuOpen || event.defaultPrevented) return;
      if (event.target instanceof Element && event.target.closest('[data-kl-scroll-locking-modal]')) return;
      event.preventDefault();
      const active = document.activeElement;
      this.closeMenu(!!active && (panel.contains(active) || active === this.menuButton()));
    }, { signal });
    // Focus moving anywhere but the panel or its button (Tab past the last link, Shift+Tab off the button) closes it.
    this.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (!this.menuOpen || !(next instanceof Node)) return;
      if (panel.contains(next) || this.menuButton()?.contains(next)) return;
      this.closeMenu();
    }, { signal });
    this.desktop.addEventListener('change', () => { if (!this.desktop.matches) this.closeMenu(); }, { signal });
  }

  /* ---------- search modal and mobile menu dialogs ---------- */
  setupDialogs(signal) {
    const menuTrigger = this.querySelector('[data-v2-menu]');
    const menuDialog = document.getElementById('xz-v2-mobile-navigation');
    const mobileDialog = menuDialog?.querySelector('dialog');
    const mobileActive = () => window.innerWidth <= 991;
    // Writes only what changes (this runs on every resize, and aria-expanded drives header styles).
    const set = (name, value) => {
      if (value === null) { if (menuTrigger.hasAttribute(name)) menuTrigger.removeAttribute(name); }
      else if (menuTrigger.getAttribute(name) !== value) menuTrigger.setAttribute(name, value);
    };
    const syncMenuMode = () => {
      if (!menuTrigger) return;
      if (mobileActive()) {
        set('role', null);
        set('aria-label', 'Open menu');
        set('aria-haspopup', 'dialog');
        if (mobileDialog?.id) set('aria-controls', mobileDialog.id);
        set('aria-expanded', String(!!mobileDialog?.open));
      } else if (this.menuPanel()?.childElementCount) {
        // 992px and up: a disclosure button for the desktop dropdown.
        set('role', 'button');
        set('aria-label', 'Menu');
        set('aria-haspopup', null);
        set('aria-controls', this.menuPanel().id);
        set('aria-expanded', String(!!this.menuOpen));
      } else {
        // No dropdown links: the plain link the server rendered.
        set('aria-label', 'Browse collections');
        for (const name of ['role', 'aria-haspopup', 'aria-controls', 'aria-expanded']) set(name, null);
      }
    };
    this.syncMenuMode = syncMenuMode;
    syncMenuMode();
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-v2-search], [data-v2-menu]');
      if (!trigger || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (trigger.matches('[data-v2-menu]') && !mobileActive()) {
        // 992px and up: the desktop dropdown (without links, the button stays a link).
        if (!this.menuPanel()?.childElementCount) return;
        event.preventDefault();
        if (this.menuOpen) this.closeMenu();
        else this.openMenu();
        return;
      }
      const dialog = trigger.matches('[data-v2-search]') ? document.getElementById('search-modal') : menuDialog;
      if (typeof dialog?.showDialog === 'function') {
        event.preventDefault();
        if (this.open) this.hide();
        this.closeMenu();
        dialog.showDialog(trigger);
        if (trigger.matches('[data-v2-menu]')) trigger.setAttribute('aria-expanded', 'true');
      }
    }, { signal });
    menuTrigger?.addEventListener('keydown', (event) => {
      const dropdown = !mobileActive() && !!this.menuPanel()?.childElementCount;
      if ((event.key === ' ' || event.key === 'Spacebar') && (mobileActive() || dropdown)) { event.preventDefault(); menuTrigger.click(); }
      else if (event.key === 'ArrowDown' && dropdown) { event.preventDefault(); this.openMenu({ focus: true }); }
    }, { signal });
    mobileDialog?.addEventListener('close', syncMenuMode, { signal });
    window.addEventListener('resize', () => {
      syncMenuMode();
      if (!this.desktop.matches) this.applyFade?.();
    }, { passive: true, signal });
  }

  /* ---------- account menu ---------- */
  setupAccount(signal) {
    const account = this.querySelector('.xz-v2-header-account');
    if (!account) return;
    const summary = account.querySelector('summary');
    const sync = () => summary.setAttribute('aria-expanded', String(account.open));
    sync();
    account.addEventListener('toggle', () => {
      sync();
      if (!account.open) return;
      if (this.open) this.hide();
      this.closeMenu();
    }, { signal });
    document.addEventListener('click', (event) => { if (account.open && !account.contains(event.target)) account.open = false; }, { signal });
    account.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && account.open) { event.stopPropagation(); account.open = false; summary.focus(); }
    }, { signal });
  }

  /* ---------- theme editor ---------- */
  setupEditor(signal) {
    if (!window.Shopify?.designMode) return;
    const revealDesktop = (key, blockId) => {
      const trigger = this.triggers().find((t) => t.dataset.v2MegaKey === key);
      if (!trigger) return false;
      this.fillPanels();
      this.show(trigger, { pinned: true });
      const panel = this.panelFor(trigger);
      if (blockId) {
        const card = [...panel.querySelectorAll('[data-v2-mobile-block-id]')].find((n) => n.dataset.v2MobileBlockId === blockId);
        card?.scrollIntoView({ block: 'nearest' });
      }
      return true;
    };
    this.revealDesktop = revealDesktop;
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target.closest?.('[data-v2-nav-block-id]');
      if (block && this.contains(block) && !this.desktop.matches && block.matches('.xz-v2-mega-trigger')) {
        document.getElementById('xz-v2-mobile-navigation')?.revealGroup?.(block.dataset.v2MegaKey);
        return;
      }
      if (block && this.contains(block) && this.desktop.matches) {
        this.setHidden(false);
        if (block.matches('.xz-v2-mega-trigger')) this.show(block, { pinned: true });
        else if (this.open) this.hide();
      }
    }, { signal });
    document.addEventListener('shopify:block:deselect', () => { if (this.open) this.hide(); }, { signal });
    window.XZV2Header = this;
  }
}

if (!customElements.get('xz-v2-header')) customElements.define('xz-v2-header', XinzuoV2Header);

// Shop by Use cards (SEO, 21 Oct 2026): the href is the crawlable type collection; the shopper's first press, key or
// focus on the card puts back its filtered link (data-xz-href), so every way of opening it (tap, click, middle or
// modified click, long-press menu, Enter) uses the preset listing. snippets/xz-v2-mobile-navigation-card.liquid.
for (const type of ['pointerdown', 'keydown', 'focusin']) {
  document.addEventListener(type, (event) => {
    const link = event.target instanceof Element && event.target.closest('a[data-xz-href]');
    if (link) { link.href = link.dataset.xzHref; link.removeAttribute('data-xz-href'); }
  }, { capture: true, passive: true });
}

/* Shopify's form-protection badge (#shop-hcaptcha-badge-container: fixed bottom right, 55px, z-index 9999) is appended to
   <body> the first time a customer form field takes focus (the newsletter on every page, contact). On phones it then sat
   over the end of the footer's legal line at the page bottom. html.xz-v2-form-badge (only while the badge exists) gives
   the footer room under it (xz-v2-extensions.css). The observer watches <body>'s own children only, no subtree. */
if (!window.__xzV2FormBadge && document.body) {
  window.__xzV2FormBadge = true;
  const root = document.documentElement;
  const syncBadge = () => {
    const on = !!document.getElementById('shop-hcaptcha-badge-container');
    if (root.classList.contains('xz-v2-form-badge') !== on) root.classList.toggle('xz-v2-form-badge', on);
  };
  new MutationObserver(syncBadge).observe(document.body, { childList: true });
  syncBadge();
}

/* Keyboard focus in a horizontal rail, site-wide (home rails, Knife Knowledge, series comparison, article related,
   bundle-builder tabs): Chrome treats a card that is only a sliver inside the page as already visible, so Tab left the
   focus ring on a card 6-40% on screen. Scroll the nearest horizontal scroller until the focused card sits inside
   both the rail and the viewport, 16px clear. Keyboard only (:focus-visible); mouse drag and touch stay as they are.
   One re-check after the (smooth) scroll settles: some rails change their own box once scrolled (the bundle tabs
   show a prev arrow). Same logic as the PDP's copy in xz-v2-ws-pdp.js, which owns product pages (and any page that
   loads that script), so this one stands down there instead of scrolling twice. */
if (!window.__xzV2RailReveal && !document.querySelector('xz-v2-pdp')) {
  window.__xzV2RailReveal = true;
  const reveal = (target, pass) => {
    if (window.__xzV2WsPdp || document.activeElement !== target) return;
    let scroller = target.parentElement;
    while (scroller && scroller !== document.body) {
      if (scroller.scrollWidth > scroller.clientWidth + 1) {
        const ox = getComputedStyle(scroller).overflowX;
        if (ox === 'auto' || ox === 'scroll') break;
      }
      scroller = scroller.parentElement;
    }
    if (!scroller || scroller === document.body) return;
    const sr = scroller.getBoundingClientRect();
    const left = Math.max(sr.left, 0) + 16;
    const right = Math.min(sr.right, document.documentElement.clientWidth) - 16;
    // The card: climb only through same-width wrappers (the link -> its card box), never into the track.
    let item = target;
    while (item.parentElement && item.parentElement !== scroller && item.parentElement.getBoundingClientRect().width <= item.getBoundingClientRect().width + 1) item = item.parentElement;
    const r = item.getBoundingClientRect();
    const delta = r.right > right ? Math.min(r.right - right, r.left - left) : (r.left < left ? r.left - left : 0);
    if (Math.abs(delta) < 1) return;
    scroller.scrollLeft += delta;
    if (pass === 0) setTimeout(() => reveal(target, 1), 450);
  };
  document.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!target || !target.matches) return;
    try { if (!target.matches(':focus-visible')) return; } catch (e) { return; }
    reveal(target, 0);
  });
}
